import type { TLShapeId } from '@tldraw/tldraw'
/**
 * Comment-pin placement rules. A comment placed on a document card is anchored
 * to it (the pin parents to the card shape) so tldraw moves the pin with the
 * card; the anchor persists as metadata.document_id + the pin's offset inside
 * the card (rel_x / rel_y), letting the pin be recreated as the card's child
 * after reload or a page change. A comment with no document_id is board-level
 * (parented to the page at absolute coords). The placement/reconcile rules are
 * pure; collectCardRects is a thin editor adapter, exercised in tests with a
 * stub editor.
 */
import type { Editor } from '@tldraw/tldraw'

/** Page-space rectangle of a rendered document card, plus its tldraw shape id. */
export interface CardRect {
  documentId: string
  shapeId: string
  x: number
  y: number
  w: number
  h: number
}

/**
 * The file a comment was dropped on. rel_x / rel_y are the pin's offset inside
 * the card and are persisted verbatim as metadata.rel_x / metadata.rel_y
 * (resolvePinPlacements reads them back under those keys), so the caller stores
 * the anchor without renaming.
 */
export interface CardAnchor {
  documentId: string
  shapeId: string
  rel_x: number
  rel_y: number
}

/** An existing pin on the canvas, reduced to what reconciliation needs. */
export interface ExistingPin {
  id: string
  commentId: string | undefined
  parentId: string
}

/** A comment as far as pin placement is concerned. */
export interface ScopableComment {
  id: string
  position_x?: number | null
  position_y?: number | null
  metadata?: Record<string, unknown> | null
}

/** Where a pin should live: under which parent, at what (parent-local) point. */
export interface PinPlacement {
  commentId: string
  /** tldraw parent id — a card shape id for file comments, the page id otherwise. */
  parentId: string
  /** Coordinates in the PARENT's space (card-relative for file comments). */
  x: number
  y: number
}

/**
 * Page-space rects of the document cards on the canvas, in z-order. The thin
 * editor adapter feeding findCardAnchorForPoint and the pin-sync scoping; takes
 * the editor so the rest of the module stays pure.
 */
export function collectCardRects(editor: Editor): CardRect[] {
  const rects: CardRect[] = []
  for (const shape of editor.getCurrentPageShapes()) {
    const documentId = shape.meta?.document_id
    if (!documentId) continue
    const bounds = editor.getShapePageBounds(shape.id)
    if (!bounds) continue
    rects.push({
      documentId: String(documentId),
      shapeId: shape.id,
      x: bounds.x,
      y: bounds.y,
      w: bounds.w,
      h: bounds.h,
    })
  }
  return rects
}

/**
 * Find the document card a canvas point lands on, returning the file id, the
 * card's shape id (the pin's parent), and the point's offset from the card's
 * top-left. Returns null for empty board background (→ board-level comment).
 * cards is expected in render (z) order; the topmost match wins on overlap.
 */
export function findCardAnchorForPoint(
  point: { x: number; y: number },
  cards: CardRect[],
): CardAnchor | null {
  let hit: CardAnchor | null = null
  for (const card of cards) {
    const within =
      point.x >= card.x &&
      point.x <= card.x + card.w &&
      point.y >= card.y &&
      point.y <= card.y + card.h
    if (within) {
      hit = {
        documentId: card.documentId,
        shapeId: card.shapeId,
        rel_x: point.x - card.x,
        rel_y: point.y - card.y,
      }
    }
  }
  return hit
}

/**
 * Decide where every comment's pin belongs right now.
 *
 * @param comments            all comments loaded for the board
 * @param cardShapeIdByDocId  document_id → card shape id, for cards on this page
 * @param pageId              the current tldraw page id (parent for board pins)
 *
 * File comments whose card isn't on the page are omitted (the card — and thus
 * the pin's parent — doesn't exist here), which is what keeps a file's comments
 * with the file instead of stranded on the board.
 */
export function resolvePinPlacements(
  comments: ScopableComment[],
  cardShapeIdByDocId: Map<string, string>,
  pageId: string,
): PinPlacement[] {
  const placements: PinPlacement[] = []
  for (const comment of comments) {
    const anchorDocId =
      typeof comment.metadata?.document_id === 'string'
        ? (comment.metadata.document_id as string)
        : undefined

    if (anchorDocId) {
      const cardShapeId = cardShapeIdByDocId.get(anchorDocId)
      if (!cardShapeId) continue // card not on this page → no pin
      placements.push({
        commentId: comment.id,
        parentId: cardShapeId,
        x: toNumber(comment.metadata?.rel_x),
        y: toNumber(comment.metadata?.rel_y),
      })
    } else if (comment.position_x != null && comment.position_y != null) {
      placements.push({
        commentId: comment.id,
        parentId: pageId,
        x: comment.position_x,
        y: comment.position_y,
      })
    }
  }
  return placements
}

/**
 * Reconcile the pins currently on the canvas against the desired placements.
 *
 * A pin is removed when its comment no longer has a placement here (deleted, or
 * a file comment whose card left the page) OR when its parent is stale — its
 * card was deleted and recreated with a new shape id on a page revisit, so the
 * pin must be recreated under the new parent. Every placement without a
 * surviving pin is (re)created.
 *
 * placements is expected to hold at most one entry per commentId (callers
 * produce one placement per comment); duplicate commentIds are collapsed to the
 * last entry.
 */
export function reconcilePins(
  existingPins: ExistingPin[],
  placements: PinPlacement[],
): { removePinIds: string[]; createCommentIds: string[] } {
  const placementByCommentId = new Map(placements.map((p) => [p.commentId, p]))
  const removePinIds: string[] = []
  const survivingCommentIds = new Set<string>()

  for (const pin of existingPins) {
    const placement = pin.commentId
      ? placementByCommentId.get(pin.commentId)
      : undefined
    if (!placement || pin.parentId !== placement.parentId) {
      removePinIds.push(pin.id)
    } else if (survivingCommentIds.has(pin.commentId as string)) {
      // UI-57: one comment, one pin. A second pin for a commentId that already
      // has one is a duplicate and goes. Two pins for one comment sit exactly
      // on top of each other, so the board looks correct until someone drags
      // the top one — and then a "copy" appears that was there all along, at
      // the original spot, opening a thread the user thinks they just moved.
      // Dropping the extra here also repairs boards that already have one.
      removePinIds.push(pin.id)
    } else {
      survivingCommentIds.add(pin.commentId as string)
    }
  }

  // Build from the deduped map keys so a commentId is never created twice (a
  // duplicate would otherwise spawn two pins for one comment).
  const createCommentIds = [...placementByCommentId.keys()].filter(
    (commentId) => !survivingCommentIds.has(commentId),
  )

  return { removePinIds, createCommentIds }
}

function toNumber(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : 0
}

/**
 * What to save after a comment pin is dragged (UI-57).
 *
 * Which fields carry a pin's position depends on what it hangs off, and getting
 * this wrong is silent: a board comment stores page coordinates in
 * position_x/position_y, while a file comment is parented to its card and
 * stores card-relative rel_x/rel_y in metadata precisely so it travels WITH the
 * card. Writing page coordinates for one of those detaches it the moment the
 * card moves, and dropping document_id turns a file comment into a board one.
 */
export function commentMoveUpdate(
  pin: { x: number; y: number; parentId: string },
  currentPageId: string,
  existingMetadata: Record<string, unknown> | undefined,
): { position_x: number; position_y: number } | { metadata: Record<string, unknown> } {
  if (pin.parentId === currentPageId) {
    return { position_x: pin.x, position_y: pin.y }
  }
  return { metadata: { ...(existingMetadata ?? {}), rel_x: pin.x, rel_y: pin.y } }
}

/**
 * The tldraw shape id for a comment's pin (UI-57).
 *
 * Derived from the comment id rather than generated, because two places create
 * pins — the comments sync effect and the create-comment flow — and they race.
 * With generated ids neither could tell the other's pin from a missing one, so
 * a new comment ended up with two pins sitting exactly on top of each other.
 * A comment can only ever have one pin now, by construction.
 *
 * tldraw requires the `shape:` prefix; comment ids are uuids, which are already
 * legal in a shape id.
 */
export function commentPinShapeId(commentId: string): TLShapeId {
  return `shape:comment-${commentId}` as TLShapeId
}
